import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import http from "../../services/http";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import { FaChartLine, FaFilter, FaLayerGroup, FaGamepad } from "react-icons/fa";
import { BsCashStack, BsTrophy, BsArrowUpRight } from "react-icons/bs";
import { FiRefreshCw } from "react-icons/fi";

export const SectionAReportLayerList = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalMatches, setTotalMatches] = useState(0);
  const [loading, setLoading] = useState(false);

  const formatNumber = (num) => {
    if (!num) return "0";
    return new Intl.NumberFormat("en-IN").format(num);
  };

  const [leagues, setLeagues] = useState([]);
  const [matches, setMatches] = useState([]);

  const [leagueFilter, setLeagueFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        league_id: leagueFilter || 0,
        match_id: matchFilter || 0,
        date_from: dateFrom || "",
        date_to: dateTo || "",
      };

      const res = await http.get("match/match-report", { params });

      if (res?.data?.status) {
        setData(res.data.report || []);
        setSummary(res.data.overall_summary || null);
        setTotalMatches(res.data.total_matches_found || 0);
      }
    } catch (err) {
      toast.error("Failed to load report");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInitial = async () => {
    try {
      const [leagueRes, matchRes] = await Promise.all([
        getLeagues(),
        getMatches(),
      ]);

      if (leagueRes?.status) setLeagues(leagueRes.data || []);
      if (matchRes?.status) setMatches(matchRes.data || []);

      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadInitial();
  }, []);

  return (
    <div className="container-fluid mt-4 px-3 px-lg-4">
      <div className="card shadow-premium border-0 animate-fade-in-up">
        {/* ===== HEADER ===== */}
        <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between align-items-center py-4 px-4 border-bottom-soft">
          <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
            <div className="bg-primary-soft p-3 rounded-4 shadow-sm">
              <FaChartLine size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="mb-0 fw-bold text-dark">Section A Reports</h4>
              <p className="text-muted small mb-0">Detailed match-wise performance analysis</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
             <span className="badge bg-primary-soft text-primary px-3 py-2 rounded-pill fw-bold">
               {totalMatches} Matches Found
             </span>
             <button className="btn btn-primary-gradient px-4 rounded-pill shadow-sm" onClick={loadData} disabled={loading}>
                <FiRefreshCw className={`me-2 ${loading ? 'spin' : ''}`} />
                Refresh
             </button>
          </div>
        </div>

        <div className="card-body p-4">

          {/* ===== OVERALL SUMMARY TILES ===== */}
          {summary && (
            <div className="row g-4 mb-5">
              <div className="col-12 col-sm-6 col-md-3 delay-1">
                <div className="summary-card grad-blue h-100 shadow-lg">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="metric-label mb-1">Collection</p>
                      <h3 className="metric-value mb-0">₹{formatNumber(summary.grand_total_collection)}</h3>
                    </div>
                    <div className="bg-white bg-opacity-20 p-2 rounded-3">
                      <BsCashStack size={22} />
                    </div>
                  </div>
                  <div className="mt-3 opacity-75 small d-flex align-items-center gap-1">
                    <BsArrowUpRight /> Revenue Stream
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-3 delay-2">
                <div className="summary-card grad-red h-100 shadow-lg">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="metric-label mb-1">Paid To Users</p>
                      <h3 className="metric-value mb-0">₹{formatNumber(summary.grand_total_paid_to_users)}</h3>
                    </div>
                    <div className="bg-white bg-opacity-20 p-2 rounded-3">
                      <FaGamepad size={22} />
                    </div>
                  </div>
                  <div className="mt-3 opacity-75 small">User Settlements</div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-3 delay-3">
                <div className="summary-card grad-green h-100 shadow-lg">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="metric-label mb-1">Admin Profit</p>
                      <h3 className="metric-value mb-0">₹{formatNumber(summary.grand_total_admin_profit)}</h3>
                    </div>
                    <div className="bg-white bg-opacity-20 p-2 rounded-3">
                      <BsTrophy size={22} />
                    </div>
                  </div>
                  <div className="mt-3 opacity-75 small font-bold text-white">Net Earnings</div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-3 delay-4">
                <div className="summary-card grad-gold h-100 shadow-lg">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="metric-label mb-1">Refunded</p>
                      <h3 className="metric-value mb-0">₹{formatNumber(summary.grand_total_refunded)}</h3>
                    </div>
                    <div className="bg-white bg-opacity-20 p-2 rounded-3">
                      <FaLayerGroup size={22} />
                    </div>
                  </div>
                  <div className="mt-3 opacity-75 small">Returned to Users</div>
                </div>
              </div>
            </div>
          )}

          {/* ===== PREMIUM FILTERS ===== */}
          <div className="premium-filter-bar p-4 rounded-4 mb-4 shadow-sm border animate-fade-in-up">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-2">
                <label className="form-label text-muted small fw-bold">From Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-2">
                <label className="form-label text-muted small fw-bold">To Date</label>
                <input
                  type="date"
                  className="form-control border-0 bg-light-soft rounded-3"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label text-muted small fw-bold">Select League</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value);
                    setMatchFilter("");
                  }}
                >
                  <option value="">All Leagues</option>
                  {leagues.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.league_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label text-muted small fw-bold">Select Match</label>
                <select
                  className="form-select border-0 bg-light-soft rounded-3"
                  value={matchFilter}
                  onChange={(e) => setMatchFilter(e.target.value)}
                >
                  <option value="">All Matches</option>
                  {matches
                    .filter((m) =>
                      leagueFilter
                        ? String(m.league_id) === String(leagueFilter)
                        : true
                    )
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.team_a} vs {m.team_b}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-12 col-md-2">
                <button
                  className="btn btn-primary w-100 rounded-pill shadow-sm py-2 fw-bold"
                  onClick={loadData}
                >
                  Apply Intelligence
                </button>
              </div>
            </div>
          </div>

          {/* ===== PREMIUM TABLE ===== */}
          <div className="table-responsive rounded-4 shadow-sm border overflow-hidden animate-fade-in-up">
            <table className="table table-hover align-middle mb-0 border-0">
              <thead className="bg-light-soft text-muted small fw-bold">
                <tr>
                  <th className="ps-4 py-3">#</th>
                  <th>Match Tracking ID</th>
                  <th>Live Status</th>
                  <th className="text-center">Total Bets</th>
                  <th className="text-end">Collection</th>
                  <th className="text-end">Admin Profit</th>
                  <th className="text-end">Side A Volume</th>
                  <th className="text-end pe-4">Side B Volume</th>
                </tr>
              </thead>

              <tbody>
                {data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={index}>
                      <td className="ps-4 text-muted small">{index + 1}</td>
                      <td>
                        <span className="fw-bold text-dark">#{item.match_info?.match_id}</span>
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${
                          item.match_info?.status === 'live' ? 'bg-success badge-live' : 'bg-secondary'
                        } px-3`}>
                          {(item.match_info?.status || 'N/A').toUpperCase()}
                        </span>
                      </td>
                      <td className="text-center">
                         <span className="badge bg-primary bg-opacity-10 text-primary px-3">{item.summary?.total_bets}</span>
                      </td>
                      <td className="text-end fw-bold text-primary font-monospace">₹{formatNumber(item.summary?.total_collection)}</td>
                      <td className="text-end">
                        <span className="badge bg-success bg-opacity-10 text-success p-2 px-3 fw-bold">₹{formatNumber(item.summary?.admin_profit)}</span>
                      </td>
                      <td className="text-end text-muted font-monospace small">₹{formatNumber(item.side_breakdown?.side_a_total_amount)}</td>
                      <td className="text-end pe-4 text-muted font-monospace small">₹{formatNumber(item.side_breakdown?.side_b_total_amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      <div className="p-4">
                        <FaLayerGroup size={40} className="opacity-25 mb-3" />
                        <p className="mb-0">No gaming data found for selected filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SectionAReportLayerList;